/*!
 * Copyright © 2026. Verizon Connect Ireland Limited. All rights reserved.
 */

import { ILatLng } from '../app/core/models';

/** Earth radius in meters (WGS-84) */
const EARTH_RADIUS_M = 6378137;

/**
 * Validates if a latitude value is within the valid range (-90 to 90 degrees)
 * @param lat - The latitude value to validate
 * @returns true if the latitude is valid, false otherwise
 */
export const isValidLatitude = (lat: number): boolean => {
  return !Number.isNaN(lat) && lat >= -90 && lat <= 90;
};

/**
 * Validates if a longitude value is within the valid range (-180 to 180 degrees)
 * @param lng - The longitude value to validate
 * @returns true if the longitude is valid, false otherwise
 */
export const isValidLongitude = (lng: number): boolean => {
  return !Number.isNaN(lng) && lng >= -180 && lng <= 180;
};

/**
 * Validates if both latitude and longitude values are within valid ranges
 * @param lat - The latitude value to validate
 * @param lng - The longitude value to validate
 * @returns true if both coordinates are valid, false otherwise
 */
export const isValidCoordinates = (lat: number, lng: number): boolean => {
  return isValidLatitude(lat) && isValidLongitude(lng);
};

/**
 * Calculates the distance in meters between two points using the equirectangular approximation.
 * Less accurate than haversine at very large distances, but significantly more efficient
 * for the short distances typical of road segments and polylines.
 *
 * @param lat1 - Latitude of the first point in degrees
 * @param lng1 - Longitude of the first point in degrees
 * @param lat2 - Latitude of the second point in degrees
 * @param lng2 - Longitude of the second point in degrees
 * @returns Distance in meters
 */
export const equirectangularDistance = (latLngA: ILatLng, latLngB: ILatLng): number => {
  const c = (latLngA.lat * Math.PI) / 180;
  const a = (latLngA.lng * Math.PI) / 180;
  const d = (latLngB.lat * Math.PI) / 180;
  const b = (latLngB.lng * Math.PI) / 180;

  const x = (b - a) * Math.cos((c + d) / 2);
  const y = d - c;
  const distance = Math.sqrt(x * x + y * y) * EARTH_RADIUS_M;
  return distance;
};

/**
 * Calculates the total distance in kilometers along a polyline of lat/lng points
 * using the equirectangular approximation.
 *
 * @param points - Array of points with lat and lng in degrees
 * @returns Total distance in kilometers, or null if fewer than 2 points
 */
export const polylineDistanceKm = (points: ILatLng[]): number | null => {
  if (!points || points.length < 2) return null;

  let totalMeters = 0;
  for (let i = 1; i < points.length; i++) {
    totalMeters += equirectangularDistance(points[i - 1], points[i]);
  }
  return totalMeters / 1000;
};
